import React, { useMemo } from 'react';
import { Box, Button, CircularProgress, Grid, Stack } from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import { FolioDrawerHeader } from '@/components/Drawers/FolioDrawer/FolioDrawerHeader';
import { FolioDrawerFormGuard } from '@/components/Drawers/FolioDrawer/FolioDrawerFormGuard';
import { FolioDrawerFooter } from '@/components/Drawers/FolioDrawer/FolioDrawerFooter';
import { useAdminConfirm } from '@/admin/shared/AdminConfirmProvider';
import { useListAllBrokerGroupsQuery } from '@/admin/brokerGroups/api/brokerGroupsApi';
import {
	useCreateUserMutation,
	useListAllUsersQuery,
	useUpdateUserMutation,
} from '../api/usersApi';
import { useListAllFeatureFlagsQuery } from '@/admin/featureFlags/api/featureFlagsApi';
import { getUserValidationSchema } from '../userValidationSchema';
import { uploadUserPhoto } from '../api/uploadUserPhoto';
import { getFeatureFlagChanges, getNewUserChanges, getNewUserFormValues } from './userFormValues';
import { UserChangeList } from './UserChangeList';
import { PhotoField } from './PhotoField';
import { DetailsFields } from './DetailsFields';
import { BrokerGroupFields } from './BrokerGroupFields';
import { LedgerFields } from './LedgerFields';
import { PermissionFields } from './PermissionFields';
import { FeatureFlagFields } from './FeatureFlagFields';
import { useApplyFeatureFlagChanges } from './useApplyFeatureFlagChanges';

/**
 * Creates a user, with the same sections as the edit drawer (UserForm), the primary broker
 * group chosen here, getUserValidationSchema, and sales team and authorised rep required
 * when needsInsightIds (the primary doesn't use clientConfigInsightSelection, and they're
 * not a Folio admin) and their Insight list has something to pick (LedgerFields). Required
 * fields are marked with an asterisk. Save lists the fields for
 * the admin to confirm, creates the user (createUser), then uploads their photo and adds
 * them to the chosen feature flags, and returns to the list. Errors the API ties to a field
 * (e.g. an email that's already a user's) show under it and keep the drawer open. If the
 * photo or a flag fails once the user exists, their edit drawer opens to try again.
 */
export const NewUserForm = ({ onClose, onCreated }) => {
	const dispatch = useDispatch();
	const confirm = useAdminConfirm();
	const [createUser] = useCreateUserMutation();
	const [updateUser] = useUpdateUserMutation();
	const applyFeatureFlagChanges = useApplyFeatureFlagChanges();
	const { data: brokerGroups, isLoading } = useListAllBrokerGroupsQuery();
	const { data: users, isLoading: areUsersLoading } = useListAllUsersQuery();
	// Loaded before the form shows, for LedgerFields' needsInsightIds and FeatureFlagFields
	const { isLoading: areFlagsLoading } = useListAllFeatureFlagsQuery();

	const validationSchema = useMemo(
		() =>
			brokerGroups &&
			users &&
			getUserValidationSchema(
				new Map(brokerGroups.map((brokerGroup) => [brokerGroup.id, brokerGroup])),
				users,
			),
		[brokerGroups, users],
	);
	const initialValues = useMemo(() => getNewUserFormValues(), []);

	if (isLoading || areUsersLoading || areFlagsLoading) {
		return (
			<Box display='flex' justifyContent='center' py={4}>
				<CircularProgress />
			</Box>
		);
	}

	const handleSubmit = async (values, { setErrors }) => {
		const { changed, after, body } = getNewUserChanges(validationSchema, values);
		const featureFlagChanges = getFeatureFlagChanges(initialValues, values);

		const confirmed = await confirm({
			title: `Create ${after.name}?`,
			content: (
				<UserChangeList
					changed={changed}
					after={after}
					featureFlagChanges={featureFlagChanges}
				/>
			),
			confirmText: 'Create',
		});
		if (!confirmed) return;

		const result = await createUser(body);
		if (result.error) {
			const fields = result.error.data?.fields;
			if (fields) {
				setErrors(fields);
				return;
			}
			dispatch(
				showToast({
					message: `Couldn't create ${after.name}. Please try again.`,
					error: true,
				}),
			);
			return;
		}
		const { id } = result.data;

		// The user exists from here, so anything that fails is retried from their edit drawer
		const failures = [];
		if (values.photoBlob) {
			try {
				const imageUrl = await uploadUserPhoto(id, values.photoBlob);
				const photoResult = await updateUser({ id, imageUrl });
				if (photoResult.error) {
					failures.push('their photo');
				}
			} catch (error) {
				console.error('Could not upload the photo', error);
				failures.push('their photo');
			}
		}
		const failedFlags = await applyFeatureFlagChanges(id, featureFlagChanges);
		if (failedFlags.length) {
			failures.push(`these feature flags: ${failedFlags.join(', ')}`);
		}

		if (failures.length) {
			dispatch(
				showToast({
					message: `${after.name} was created, but we couldn't save ${failures.join(' or ')}. Please try again.`,
					error: true,
				}),
			);
			onCreated(id);
			return;
		}
		dispatch(showToast({ message: `${after.name} was created.`, success: true }));
		onClose();
	};

	return (
		<Stack sx={{ flex: 1, minHeight: 0, height: '100%', overflow: 'hidden', pt: 1 }}>
			<FolioDrawerHeader title='New user' />
			<Formik
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={handleSubmit}
			>
				{({ values, isSubmitting }) => (
					<Form
						style={{
							display: 'flex',
							flexDirection: 'column',
							flex: 1,
							minHeight: 0,
							width: '100%',
							height: '100%',
							overflow: 'hidden',
						}}
					>
						<Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2 }}>
							<Grid container spacing={2}>
								<PhotoField />
								<DetailsFields />
								<BrokerGroupFields canChangePrimary />
								<LedgerFields isNew />
								<PermissionFields />
								<FeatureFlagFields
									brokerGroupIds={[values.brokerGroupId, ...values.brokerGroupArray].filter(
										Boolean,
									)}
									ledger={
										brokerGroups.find(({ id }) => id === values.brokerGroupId)?.ledger
									}
								/>
							</Grid>
						</Box>
						<FolioDrawerFormGuard />
						<FolioDrawerFooter>
							{/* Enabled while invalid: submitting touches every field, so each error
								shows, and Formik only calls handleSubmit once the form is valid */}
							<Button type='submit' variant='contained' disabled={isSubmitting}>
								{/* Submitting runs until the user, photo and flags are all saved */}
								{isSubmitting ? <CircularProgress size={18} color='inherit' /> : 'Create'}
							</Button>
						</FolioDrawerFooter>
					</Form>
				)}
			</Formik>
		</Stack>
	);
};

NewUserForm.displayName = 'NewUserForm';
